import type {
  CatalogV2ListRow,
  CatalogV2Variation,
} from '@skupreme/api-contracts/core';
import { Cell, Row, RowExpandButton } from '@skupreme/shared/ui';
import { type ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useCatalogV2Variations } from '../../hooks/use-catalog-v2-variations';

/** "3 variants", "1 variant". */
export const variantCountLabel = (count: number) =>
  `${count} ${count === 1 ? 'variant' : 'variants'}`;

/* A nested row has the product, then one empty cell spanning the rest of the header. */
function VariantRow({
  variant,
  parentId,
  columnCount,
}: {
  variant: CatalogV2Variation;
  parentId: string;
  columnCount: number;
}) {
  const navigate = useNavigate();

  return (
    <Row
      id={`${parentId}/${variant.id}`}
      isNested
      onAction={() =>
        navigate(`/app/catalogs/catalog/${encodeURIComponent(variant.id)}`)
      }
    >
      <Cell>
        <div className="sk:flex sk:min-w-0 sk:flex-col">
          <Link
            to={`/app/catalogs/catalog/${encodeURIComponent(variant.id)}`}
            className="sk:truncate sk:font-medium sk:text-fg sk:no-underline"
          >
            {variant.title}
          </Link>
          {variant.sku ? (
            <span className="sk:truncate sk:text-dense sk:text-fg-secondary">
              {variant.sku}
            </span>
          ) : null}
          <span className="sk:truncate sk:text-xs sk:text-fg-tertiary">
            {variant.variant_kind}
          </span>
        </div>
      </Cell>
      <Cell colSpan={columnCount - 1} />
    </Row>
  );
}

/** One nested row that spans the whole header: the loading placeholder and the error. */
function MessageRow({
  id,
  columnCount,
  children,
}: {
  id: string;
  columnCount: number;
  children?: ReactNode;
}) {
  return (
    <Row id={id} isNested>
      <Cell colSpan={columnCount}>{children}</Cell>
    </Row>
  );
}

/** What an open parent shows under it: the error, the placeholder while loading, or its variants. */
function NestedRows({
  parentId,
  columnCount,
  isError,
  data,
}: {
  parentId: string;
  columnCount: number;
  isError: boolean;
  data?: CatalogV2Variation[];
}) {
  if (isError) {
    return (
      <MessageRow
        key="error"
        id={`${parentId}/error`}
        columnCount={columnCount}
      >
        Couldn't load variants
      </MessageRow>
    );
  }
  if (!data) {
    return (
      <MessageRow
        key="loading"
        id={`${parentId}/loading`}
        columnCount={columnCount}
      >
        <div className="sk:h-4 sk:w-1/3 sk:animate-pulse sk:rounded-option sk:bg-fill-secondary" />
      </MessageRow>
    );
  }

  return data.map(variant => (
    <VariantRow
      key={variant.id}
      variant={variant}
      parentId={parentId}
      columnCount={columnCount}
    />
  ));
}

/**
 * A parent with variants: its own row, built by `renderParent` with the expand
 * control, and while it is open, its variants right under it.
 */
export function CatalogV2Variants({
  row,
  columnCount,
  renderParent,
}: {
  row: CatalogV2ListRow;
  /** The header's column count, so a nested row has as many columns. */
  columnCount: number;
  renderParent: (expand: ReactNode) => ReactNode;
}) {
  const [isExpanded, setExpanded] = useState(false);
  const variants = useCatalogV2Variations(row.id, isExpanded);

  return (
    <>
      {renderParent(
        <RowExpandButton
          isExpanded={isExpanded}
          onExpandedChange={setExpanded}
          aria-label={`${isExpanded ? 'Hide' : 'Show'} variants of ${row.sku || row.title}`}
          className="sk:ml-auto"
        />,
      )}
      {isExpanded ? (
        <NestedRows
          parentId={row.id}
          columnCount={columnCount}
          isError={variants.isError}
          data={variants.data}
        />
      ) : null}
    </>
  );
}
